import React, { PropsWithChildren, ReactNode } from 'react';
import './FormPageFooter.scss';
import { Button, YoshikiFrame } from '@src/components';

export interface IFormPagesLayoutFooterButton {
  text: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
  enabled: boolean;
  loading?: boolean;
  buttonStyle?: 'normal' | 'subtle' | 'outline' | 'wide' | 'search' | 'edit';
  rightArrow?: boolean;
}

export interface FormPagesLayoutFooterProps {
  nextButton?: IFormPagesLayoutFooterButton;
  previousButton?: IFormPagesLayoutFooterButton;
  previousButtonWithText?: IFormPagesLayoutFooterButton;
  className?: string;
  beforeMessages?: ReactNode[];
  afterMessages?: ReactNode[];
  largeAfterMessages?: ReactNode[];
  sra?: boolean;
  privacyMessage?: (string | JSX.Element)[];
  errorMessage?: string;
}

export function FormPageFooter({
  children,
  nextButton,
  previousButton,
  previousButtonWithText,
  className,
  beforeMessages,
  afterMessages,
  largeAfterMessages,
  sra,
  privacyMessage,
  errorMessage,
}: PropsWithChildren<FormPagesLayoutFooterProps>) {
  return (
    <div className={`form-page-footer ${className || ''}`}>
      {children}

      {errorMessage && (
        <div className='form-column form-page-footer--lock-messages'>
          <div className='form-page-footer--error-message'>{errorMessage}</div>
        </div>
      )}

      {beforeMessages && (
        <div className='form-column form-page-footer--lock-messages'>
          {beforeMessages.map((value, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className='form-page-footer--lock-message' key={index}>
              <span className='form-page-footer--lock-icon' />
              {value}
            </div>
          ))}
        </div>
      )}

      {privacyMessage && (
        <div className='form-column form-page-footer--lock-messages'>
          <div className='form-page-footer--lock-message'>
            <span className='form-page-footer--lock-icon' />
            {privacyMessage}
          </div>
        </div>
      )}

      {sra && (
        <div className='form-page-footer--sra-container'>
          <div className='form-page-footer--sra-image'>
            <YoshikiFrame />
          </div>
        </div>
      )}

      <div className='form-page-footer-button-wrapper'>
        {previousButton && (
          <Button
            classNames='form-page-footer-back-button'
            leftArrow
            disabled={!previousButton.enabled}
            onClick={previousButton.onClick}
            text={previousButton.text}
            buttonStyle='subtle'
          />
        )}
        {previousButtonWithText && (
          <Button
            classNames='form-page-footer-back-button-with-text'
            leftArrow
            disabled={!previousButtonWithText.enabled}
            onClick={previousButtonWithText.onClick}
            text={previousButtonWithText.text}
            buttonStyle='subtle'
          />
        )}

        {nextButton && (
          <Button
            classNames='form-page-footer-next-button'
            rightArrow={nextButton.rightArrow}
            disabled={!nextButton.enabled}
            onClick={nextButton.onClick}
            text={nextButton.text}
            loading={!!nextButton.loading}
            buttonStyle={nextButton.buttonStyle || 'normal'}
          />
        )}
      </div>

      {largeAfterMessages && (
        <div className='form-column form-page-footer--lock-messages'>
          {largeAfterMessages.map((value, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className='form-page-footer--lock-message-large' key={index}>
              {value}
            </div>
          ))}
        </div>
      )}

      {afterMessages && (
        <div className='form-column form-page-footer--lock-messages'>
          {afterMessages.map((value, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className='form-page-footer--lock-message' key={index}>
              <span className='form-page-footer--lock-icon' />
              {value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
